/**
 * To be used with `useBreakpoints` from VueUse
 */
export const appBreakpoints = {
  // Tailwind default breakpoints
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
  // Custom breakpoints
  desktop: "820px",
  xs: "425px",
  "2xs": "375px",
  "large-desktop": "1024px",
};

// ---------------------------------------------------------------------------
// Design Tokens
// ---------------------------------------------------------------------------

/**
 * The canonical design token interface for the FanFest theme system.
 *
 * Each property maps 1:1 to a CSS custom property (e.g. `bg` -> `--bg`) and a
 * Tailwind color utility (e.g. `bg-bg`, `text-text-secondary`, `border-border-brand`).
 *
 * Token categories:
 *  - `bg_*`      — page/app background colors
 *  - `surface_*` — card/container surface colors
 *  - `fill_*`    — interactive element fills (buttons, toggles, badges)
 *  - `text_*`    — text colors
 *  - `border_*`  — borders and dividers
 *  - `icon_*`    — icon colors
 */
export interface DesignTheme {
  // Background
  bg: string;
  bg_secondary: string;

  // Surface
  surface: string;
  surface_hover: string;
  surface_secondary: string;
  surface_secondary_hover: string;
  surface_accent: string;

  // Fill
  fill_brand: string;
  fill_brand_hover: string;
  fill_brand_pressed: string;
  fill_brand_secondary: string;
  fill_brand_secondary_hover: string;
  fill_brand_secondary_pressed: string;
  fill_dark: string;
  fill_dark_hover: string;
  fill_dark_pressed: string;
  fill_disabled: string;
  fill_success: string;
  fill_critical: string;
  fill_critical_secondary: string;
  fill_warning: string;
  fill_warning_secondary: string;
  fill_silver: string;
  fill_bronze: string;
  fill_gold: string;
  fill_gold_light: string;
  fill_gold_dark: string;

  // Text
  text: string;
  text_on_brand: string;
  text_inverse: string;
  text_secondary: string;
  text_tertiary: string;
  text_disabled: string;
  text_success: string;
  text_critical: string;
  text_brand: string;
  text_accent: string;
  text_accent_secondary: string;
  text_gold: string;
  text_gold_light: string;
  text_gold_dark: string;

  // Border
  border: string;
  border_hover: string;
  border_pressed: string;
  border_selected: string;
  border_secondary: string;
  border_brand: string;
  border_brand_hover: string;
  border_brand_pressed: string;
  border_brand_selected: string;
  border_critical: string;
  border_success: string;
  border_warning: string;
  border_dark: string;
  border_button_shadow_light: string;
  border_button_shadow_dark: string;

  // Icon
  icon: string;
  icon_emphasis: string;
  icon_on_branded_surface: string;
  icon_hover: string;
  icon_secondary: string;
  icon_secondary_hover: string;
  icon_disabled: string;
  icon_brand: string;
}

/**
 * All design token keys, useful for iterating over the token set.
 */
export const DESIGN_TOKEN_KEYS: readonly (keyof DesignTheme)[] = [
  "bg",
  "bg_secondary",
  "surface",
  "surface_hover",
  "surface_secondary",
  "surface_secondary_hover",
  "surface_accent",
  "fill_brand",
  "fill_brand_hover",
  "fill_brand_pressed",
  "fill_brand_secondary",
  "fill_brand_secondary_hover",
  "fill_brand_secondary_pressed",
  "fill_dark",
  "fill_dark_hover",
  "fill_dark_pressed",
  "fill_disabled",
  "fill_success",
  "fill_critical",
  "fill_critical_secondary",
  "fill_warning",
  "fill_warning_secondary",
  "fill_silver",
  "fill_bronze",
  "fill_gold",
  "fill_gold_light",
  "fill_gold_dark",
  "text",
  "text_on_brand",
  "text_inverse",
  "text_secondary",
  "text_tertiary",
  "text_disabled",
  "text_success",
  "text_critical",
  "text_brand",
  "text_accent",
  "text_accent_secondary",
  "text_gold",
  "text_gold_light",
  "text_gold_dark",
  "border",
  "border_hover",
  "border_pressed",
  "border_selected",
  "border_secondary",
  "border_brand",
  "border_brand_hover",
  "border_brand_pressed",
  "border_brand_selected",
  "border_critical",
  "border_success",
  "border_warning",
  "border_dark",
  "border_button_shadow_light",
  "border_button_shadow_dark",
  "icon",
  "icon_emphasis",
  "icon_on_branded_surface",
  "icon_hover",
  "icon_secondary",
  "icon_secondary_hover",
  "icon_disabled",
  "icon_brand",
] as const;

/**
 * Converts a DesignTheme token key (snake_case) to its CSS custom property name.
 * Example: "fill_brand_hover" -> "--fill-brand-hover"
 */
export function tokenToCssVar(token: keyof DesignTheme): string {
  return `--${token.replace(/_/g, "-")}`;
}

/**
 * Converts a DesignTheme token key (snake_case) to its Tailwind color key.
 * Example: "fill_brand_hover" -> "fill-brand-hover"
 */
export function tokenToTailwindKey(token: keyof DesignTheme): string {
  return token.replace(/_/g, "-");
}

// ---------------------------------------------------------------------------
// Legacy Token Mapping
// ---------------------------------------------------------------------------

/**
 * Maps legacy (v1) token names to their new (v2) design token equivalents.
 *
 * These mappings document which old token was replaced by which new token.
 * The legacy tokens are preserved as backwards-compatible aliases in the
 * Tailwind config and CSS custom properties so existing components continue
 * to work during migration.
 *
 * Legacy CSS var                   -> New CSS var               | Notes
 * ─────────────────────────────────────────────────────────────────────────
 * --brand-bg-main                  -> --bg                      | Main app background
 * --brand-primary                  -> --fill-brand              | Primary brand color for fills
 * --brand-primary                  -> --text-brand              | Primary brand color for text
 * --brand-primary                  -> --border-brand            | Primary brand color for borders
 * --brand-primary                  -> --icon-brand              | Primary brand color for icons
 * --brand-surface-hover            -> --surface-hover           | Surface hover state
 * --brand-bgdivider-10             -> --surface                 | Subtle surface / divider bg
 * --brand-bgdivider-20             -> --surface-hover           | Slightly stronger divider bg
 * --brand-quaternary-40            -> --fill-brand-secondary    | 40% opacity brand fill
 * --brand-secondary-80             -> --fill-brand-hover        | 80% opacity brand (hover)
 * --brand-tertiary-60              -> --fill-brand-pressed      | 60% opacity brand (pressed)
 * --brand-fill-dark                -> --fill-dark               | Dark fill overlay
 * --brand-icon-emphasis            -> --icon-emphasis           | Emphasized icon color
 * --brand-strong-bg-overlay-80     -> --bg-secondary            | Strong bg overlay
 * --brand-light-bg-overlay-50      -> --bg-secondary            | Light bg overlay (similar usage)
 * --basics-primary                 -> --text                    | Primary text color
 * --basics-secondary-80            -> --text-secondary          | Secondary text color
 * --basics-tertiary-60             -> --text-tertiary           | Tertiary text color
 * --basics-quaternary-40           -> --text-disabled           | Disabled / quaternary text
 * --basics-bgdivider-5             -> --border                  | Subtle border / divider
 * --basics-bgdivider-10            -> --border-hover            | Border hover state
 * --basics-bgdivider-20            -> --border-pressed          | Border pressed state
 * --basics-accent-orange           -> --fill-warning            | Warning / accent orange
 * --basics-accent-red              -> --fill-critical           | Critical / accent red
 * --basics-accent-green            -> --fill-success            | Success / accent green
 * --basics-accent-gold             -> --fill-gold               | Gold accent
 */
